/**
 * Connector Flow State Management
 * Manages state persistence across the multi-step connector creation flow
 */

import { getResolvedWorkspaceId, getWorkspaceIdFromLocation } from '@/lib/active-workspace';

const FLOW_STATE_CHANGED_EVENT = 'connector-flow-state:changed';
export const DEFAULT_FLOW_SYSTEM_NAME = 'System';

function normalizeSystemId(systemId?: string | null): string {
  return systemId?.trim() ?? '';
}

export function isTemporarySystemId(systemId?: string | null): boolean {
  const normalizedSystemId = normalizeSystemId(systemId);
  return /^temp(?:_|-)/.test(normalizedSystemId);
}

export function isPersistedSystemId(systemId?: string | null): boolean {
  const normalizedSystemId = normalizeSystemId(systemId);
  return Boolean(normalizedSystemId) && !isTemporarySystemId(normalizedSystemId);
}

export interface ConnectorFlowState {
  // Step tracking
  visitedSteps: number[];
  currentStep: number;

  // Step 1: Connector Profile
  connectorData: {
    name: string;
    description?: string;
  } | null;

  // Step 2: Add API
  previewActions: unknown[];
  hasProcessedApi: boolean;
  uploadedFileUrl?: string;
  // Step 3: Authentication. Persist only a non-secret dirty marker; credential
  // values stay in component memory and are never written to browser storage.
  hasAuthDraft: boolean;

  // Metadata
  systemId: string | null;
  creationSystemId?: string | null;
  systemName: string;
  isTemporary: boolean;
  createdAt: number;
}

const STORAGE_KEY = 'connector_flow_state';
let hasScrubbedStoredFlowStates = false;

function getScopedStorageKey(workspaceId?: string | null): string {
  const normalizedWorkspaceId = workspaceId?.trim();
  return normalizedWorkspaceId ? `${STORAGE_KEY}:${normalizedWorkspaceId}` : STORAGE_KEY;
}

function getCurrentStorageKey(workspaceId?: string | null): string {
  return getScopedStorageKey(getResolvedWorkspaceId(workspaceId));
}

function readStoredState(storageKey: string): string | null {
  const scopedValue = localStorage.getItem(storageKey) ?? sessionStorage.getItem(storageKey);
  if (scopedValue) {
    return scopedValue;
  }

  if (storageKey === STORAGE_KEY) {
    return null;
  }

  return localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
}

export function getDefaultFlowState(): ConnectorFlowState {
  return {
    visitedSteps: [1],
    currentStep: 1,
    connectorData: null,
    previewActions: [],
    hasProcessedApi: false,
    uploadedFileUrl: undefined,
    hasAuthDraft: false,
    systemId: null,
    creationSystemId: null,
    systemName: DEFAULT_FLOW_SYSTEM_NAME,
    isTemporary: true,
    createdAt: Date.now(),
  };
}

function sanitizeFlowState(value: unknown): ConnectorFlowState {
  const defaults = getDefaultFlowState();
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return defaults;
  }

  const candidate = value as Partial<ConnectorFlowState> & {
    authConfig?: unknown;
  };
  return {
    visitedSteps: Array.isArray(candidate.visitedSteps)
      ? candidate.visitedSteps.filter((step): step is number => Number.isInteger(step) && step > 0)
      : defaults.visitedSteps,
    currentStep:
      typeof candidate.currentStep === 'number' &&
      Number.isInteger(candidate.currentStep) &&
      candidate.currentStep > 0
        ? candidate.currentStep
        : defaults.currentStep,
    connectorData:
      candidate.connectorData && typeof candidate.connectorData === 'object'
        ? candidate.connectorData
        : null,
    previewActions: Array.isArray(candidate.previewActions) ? candidate.previewActions : [],
    hasProcessedApi: candidate.hasProcessedApi === true,
    uploadedFileUrl:
      typeof candidate.uploadedFileUrl === 'string' ? candidate.uploadedFileUrl : undefined,
    hasAuthDraft: candidate.hasAuthDraft === true || Boolean(candidate.authConfig),
    systemId: typeof candidate.systemId === 'string' ? candidate.systemId : null,
    creationSystemId:
      typeof candidate.creationSystemId === 'string' ? candidate.creationSystemId : null,
    systemName:
      typeof candidate.systemName === 'string' ? candidate.systemName : defaults.systemName,
    isTemporary: candidate.isTemporary !== false,
    createdAt: typeof candidate.createdAt === 'number' ? candidate.createdAt : defaults.createdAt,
  };
}

function persistSanitizedFlowState(storageKey: string, state: ConnectorFlowState): void {
  const serializedState = JSON.stringify(sanitizeFlowState(state));
  localStorage.setItem(storageKey, serializedState);
  sessionStorage.setItem(storageKey, serializedState);

  if (storageKey !== STORAGE_KEY) {
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

function scrubAllStoredFlowStates(): void {
  if (hasScrubbedStoredFlowStates || typeof window === 'undefined') return;

  try {
    const keys = new Set<string>();
    for (const storage of [localStorage, sessionStorage]) {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (key === STORAGE_KEY || key?.startsWith(`${STORAGE_KEY}:`)) {
          keys.add(key);
        }
      }
    }

    for (const key of keys) {
      const stored = localStorage.getItem(key) ?? sessionStorage.getItem(key);
      if (!stored) continue;

      try {
        const sanitizedState = sanitizeFlowState(JSON.parse(stored));
        const serializedState = JSON.stringify(sanitizedState);
        localStorage.setItem(key, serializedState);
        sessionStorage.setItem(key, serializedState);
      } catch {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      }
    }

    hasScrubbedStoredFlowStates = true;
  } catch {
    console.error('Failed to scrub connector flow state storage');
  }
}

export function getFlowStateConnectorName(flowState: ConnectorFlowState): string {
  const connectorName = flowState.connectorData?.name?.trim();
  if (connectorName) {
    return connectorName;
  }

  const systemName = flowState.systemName?.trim() ?? '';
  const isDefaultPlaceholder =
    systemName === DEFAULT_FLOW_SYSTEM_NAME && !flowState.connectorData && !flowState.systemId;

  return isDefaultPlaceholder ? '' : systemName;
}

export function getFlowState(workspaceId?: string | null): ConnectorFlowState {
  if (typeof window === 'undefined') {
    return getDefaultFlowState();
  }
  scrubAllStoredFlowStates();

  try {
    const storageKey = getCurrentStorageKey(workspaceId);
    const stored = readStoredState(storageKey);
    if (stored) {
      const sanitizedState = sanitizeFlowState(JSON.parse(stored));
      const serializedState = JSON.stringify(sanitizedState);
      if (
        localStorage.getItem(storageKey) !== serializedState ||
        sessionStorage.getItem(storageKey) !== serializedState ||
        (storageKey !== STORAGE_KEY &&
          (localStorage.getItem(STORAGE_KEY) !== null ||
            sessionStorage.getItem(STORAGE_KEY) !== null))
      ) {
        persistSanitizedFlowState(storageKey, sanitizedState);
      }
      return sanitizedState;
    }
  } catch (error) {
    console.error('Failed to parse connector flow state:', error);
  }

  return getDefaultFlowState();
}

export function setFlowState(state: ConnectorFlowState, workspaceId?: string | null): void {
  if (typeof window === 'undefined') return;
  scrubAllStoredFlowStates();

  try {
    const storageKey = getCurrentStorageKey(workspaceId);
    persistSanitizedFlowState(storageKey, state);

    window.dispatchEvent(new Event(FLOW_STATE_CHANGED_EVENT));
  } catch (error) {
    console.error('Failed to save connector flow state:', error);
  }
}

/**
 * Rebuilds the draft of a connector the server holds but this browser has no
 * draft for, so the creation modal continues it: at Add API when it has no
 * saved actions, at Authentication when it has. The draft never claims the
 * connector as created by this flow (no creationSystemId), so discarding it
 * cannot delete a connector someone else may still be finishing.
 */
export function resumeConnectorFlow(
  connector: {
    system_id: string;
    name: string;
    description?: string;
    api_spec_count?: number;
    spec_count?: number;
  },
  workspaceId?: string | null
): ConnectorFlowState {
  const hasActions = (connector.api_spec_count ?? connector.spec_count ?? 0) > 0;
  const name = connector.name.trim();
  const state: ConnectorFlowState = {
    ...getDefaultFlowState(),
    visitedSteps: hasActions ? [1, 2, 3] : [1, 2],
    currentStep: hasActions ? 3 : 2,
    connectorData: { name, description: connector.description?.trim() ?? '' },
    systemId: connector.system_id,
    systemName: name,
    isTemporary: false,
  };
  setFlowState(state, workspaceId);
  return state;
}

export function updateFlowState(
  updates: Partial<ConnectorFlowState>,
  workspaceId?: string | null
): ConnectorFlowState {
  const current = getFlowState(workspaceId);
  const updated = { ...current, ...updates };
  setFlowState(updated, workspaceId);
  return updated;
}

export function markStepVisited(
  stepNumber: number,
  workspaceId?: string | null
): ConnectorFlowState {
  const state = getFlowState(workspaceId);
  if (!state.visitedSteps.includes(stepNumber)) {
    state.visitedSteps = [...state.visitedSteps, stepNumber].sort((a, b) => a - b);
  }
  state.currentStep = stepNumber;
  setFlowState(state, workspaceId);
  return state;
}

export function isStepEnabled(stepNumber: number, workspaceId?: string | null): boolean {
  const state = getFlowState(workspaceId);
  return state.visitedSteps.includes(stepNumber);
}

export function getMaxVisitedStep(workspaceId?: string | null): number {
  const state = getFlowState(workspaceId);
  return Math.max(...state.visitedSteps, 1);
}

export function clearFlowState(workspaceId?: string | null): void {
  if (typeof window === 'undefined') return;
  scrubAllStoredFlowStates();

  const storageKey = getCurrentStorageKey(workspaceId);

  localStorage.removeItem(storageKey);
  sessionStorage.removeItem(storageKey);
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);

  // Explicit discard must also clear the URL-scoped key when workspace
  // resolution changed between the original write and the dismissal.
  const locationWorkspaceId = getWorkspaceIdFromLocation();
  if (locationWorkspaceId) {
    const locationStorageKey = getScopedStorageKey(locationWorkspaceId);
    localStorage.removeItem(locationStorageKey);
    sessionStorage.removeItem(locationStorageKey);
  }

  // Also clear legacy keys for backward compatibility
  localStorage.removeItem('connectorData');
  localStorage.removeItem('previewActions');
  localStorage.removeItem('systemName');
  sessionStorage.removeItem('connectorData');
  sessionStorage.removeItem('previewActions');
  sessionStorage.removeItem('systemName');

  window.dispatchEvent(new Event(FLOW_STATE_CHANGED_EVENT));
}

export function subscribeToFlowStateChanges(onChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleFlowStateChanged = () => {
    onChange();
  };

  const handleStorageChanged = (event: StorageEvent) => {
    if (event.key === null || event.key.startsWith(STORAGE_KEY)) {
      onChange();
    }
  };

  const handlePageShow = () => {
    onChange();
  };

  const handleVisibilityChanged = () => {
    if (document.visibilityState === 'visible') {
      onChange();
    }
  };

  window.addEventListener(FLOW_STATE_CHANGED_EVENT, handleFlowStateChanged);
  window.addEventListener('storage', handleStorageChanged);
  window.addEventListener('pageshow', handlePageShow);
  document.addEventListener('visibilitychange', handleVisibilityChanged);

  return () => {
    window.removeEventListener(FLOW_STATE_CHANGED_EVENT, handleFlowStateChanged);
    window.removeEventListener('storage', handleStorageChanged);
    window.removeEventListener('pageshow', handlePageShow);
    document.removeEventListener('visibilitychange', handleVisibilityChanged);
  };
}

export function hasUnsavedChanges(): boolean {
  const state = getFlowState();

  const systemCreatedDuringFlow = Boolean(getFlowCreationSystemId(state, state.systemId));

  return !!(
    state.connectorData ||
    state.previewActions.length > 0 ||
    state.hasAuthDraft ||
    systemCreatedDuringFlow
  );
}

/** Returns a persisted system id with explicit connector-creation provenance. */
export function getFlowCreationSystemId(
  state: ConnectorFlowState,
  candidateSystemId?: string | null
): string | null {
  const candidate = normalizeSystemId(candidateSystemId ?? state.systemId);
  if (!isPersistedSystemId(candidate) || normalizeSystemId(state.systemId) !== candidate) {
    return null;
  }

  return normalizeSystemId(state.creationSystemId) === candidate ? candidate : null;
}

export function isConnectorCreationFlowSystem(
  state: ConnectorFlowState,
  candidateSystemId?: string | null
): boolean {
  return Boolean(getFlowCreationSystemId(state, candidateSystemId));
}

// Migrate from old sessionStorage format to new format
export function migrateFromLegacyStorage(): ConnectorFlowState {
  const state = getDefaultFlowState();

  try {
    const connectorData = sessionStorage.getItem('connectorData');
    if (connectorData) {
      state.connectorData = JSON.parse(connectorData);
    }

    const previewActions = sessionStorage.getItem('previewActions');
    if (previewActions) {
      state.previewActions = JSON.parse(previewActions);
      state.hasProcessedApi = true;
    }

    const systemName = sessionStorage.getItem('systemName');
    if (systemName) {
      state.systemName = systemName;
    }
  } catch (error) {
    console.error('Failed to migrate legacy storage:', error);
  }

  setFlowState(state);
  return state;
}
